import { useCallback, useEffect, useState } from 'react'
import { useParams } from "react-router-dom"
import { faDownload, faEye } from '@fortawesome/free-solid-svg-icons'

import { message, Table, Result, Button } from 'antd'

import ButtonPrimary from "components/ButtonPrimary"
import Loading from 'components/Loading'
import ViewPDF from "components/ViewPDF"

import { discectError, getSuretyLogo } from 'lib/utils'
import api from 'lib/api'

import styles from './styles.module.scss'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

const StatusDownload = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(null)
  const [download, setDownload] = useState(null)
  const [downloadContent, setDownloadContent] = useState(null)
  const [isMobile, setIsMobile] = useState(true)
  const [viewPdf, setViewPdf] = useState(null)

  const { downloadId } = useParams()

  const [messageApi, contextHolder] = message.useMessage()

  const _fetchDownload = useCallback(async () => {
    setIsLoading(true)
    try {
      const { download: incomingDownload } = await api.inventory.getDownload(downloadId)
      setDownload(incomingDownload)
    } catch(e) {
      console.error(e)
      messageApi.open({
        type: 'error',
        content: discectError(e)
      })
    }
    setIsLoading(false)
  }, [downloadId, messageApi])

  useEffect(() => {
    if (!download) _fetchDownload()
  }, [_fetchDownload, downloadId, download, messageApi])

  useEffect(() => {
    window.mobileCheck() ? setIsMobile(true) : setIsMobile(false)
  }, [])

  const _onFetchDownloadContent = useCallback(async () => {
    setIsLoading(true)
    try {
      const { summary } = await api.inventory.download(downloadId, true, {})
      setDownloadContent({ summary })
    } catch(e) {
      console.error(e)
      let error = discectError(e)
      let errorText = 'General Error'
      if (error.indexOf('DOCUMENT_ALREADY_DELETED') >= 0) {
        errorText = 'Invalid Document. Please contact your Surety.'
      } else if (error.indexOf('DOCUMENT_EXPIRED') >= 0) {
        errorText = 'This time on this document has expired. Please contact your Surety for a new Link.'
      } else if (error.indexOf('INVALID_SECURITY_CODE') >= 0) {
        errorText = 'This security code you entered is invalid. Please contact your Surety for a new Code.'
      } else if (error.indexOf('INVALID_DOCUMENT_ID') >= 0) {
        errorText = 'This is an invalid document. Please contact your Surety.'
      }
      setError(errorText)
    }
    setIsLoading(false)
  }, [downloadId])

  useEffect(() => {
    if (!downloadContent) _onFetchDownloadContent()
  }, [_onFetchDownloadContent, downloadId, downloadContent, messageApi])

  const _doDownloadZip = async (params = {}) => {
    setIsLoading(true)
    try {
      const { url } = await api.inventory.download(downloadId, false, params)
      window.open(url, "_blank")
      _fetchDownload()
    } catch(e) {
      console.error(e)
      let error = discectError(e)
      let errorText = 'General Error'
      if (error.indexOf('DOCUMENT_ALREADY_DELETED') >= 0) {
        errorText = 'Invalid Document. Please contact your Surety.'
      } else if (error.indexOf('DOCUMENT_EXPIRED') >= 0) {
        errorText = 'This time on this document has expired. Please contact your Surety for a new Link.'
      } else if (error.indexOf('INVALID_SECURITY_CODE') >= 0) {
        errorText = 'This security code you entered is invalid. Please contact your Surety for a new Code.'
      } else if (error.indexOf('INVALID_DOCUMENT_ID') >= 0) {
        errorText = 'This is an invalid document. Please contact your Surety.'
      }
      setError(errorText)
    }
    setIsLoading(false)
  }

  const _getTable = () => {
    const columns = [
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        align: 'left',
        render: (_, { type }) => {
          return (
            <div className={styles.agencyInfo}>
              <p>{type}</p>
            </div>
          )
        }
      },
      {
        title: 'Pages',
        dataIndex: 'pages',
        key: 'pages',
        width: 80,
        align: 'center',
        render: (_, { pages }) => {
          return (
            <div className={styles.agencyInfo}>
              <p>{pages}</p>
            </div>
          )
        }
      },
      {
        title: 'Actions',
        key: 'action',
        width: 110,
        render: (_, record) => {
          return (
            <div className={styles.tableActions}>
              <ButtonPrimary
                small
                color="navy"
                onClick={() => _doDownloadZip({ key: record.key })}
                icon={faDownload}
                disabled={isMobile}
                tooltip={isMobile ? "Downloading is not supported on mobile devices" : null}
              >
                Download
              </ButtonPrimary>     
            </div>
          )
        }
      }
    ]

    return (
      <Table
        bordered
        dataSource={download?.additionalFiles}
        columns={columns}
        size="small"
        pagination={false}
      />
    )
  }

  const _getViewPdf = () => {
    if (viewPdf) {
      return (
        <ViewPDF 
          isWatermarkDisabled
          url={viewPdf}
          text="Print the Power by clicking the Print Button"
          onClose={() => setViewPdf(null)}
        />
      )
    }
    return null
  }

  return (
    <>
      {contextHolder}
      <div className={styles.receipt__container}>
        <div className={styles.receipt}>
          {/* {isExpired && (
            <Result
              status="error"
              title="Expired!"
              subTitle={`The link you are trying to download has expired. Please contact your Surety for a new Link.`}
            />
          )} */}
          {error && (
            <Result
              status="error"
              title="Submission Failed"
              subTitle={error}
              extra={[
                <Button onClick={() => setError(null)}>
                  Go Back
                </Button>
              ]}
            ></Result>
          )}
          {(!error) && (
            <>
              <header>
                {getSuretyLogo(download?.surety?.fb_id, download?.surety?.name)}
                <p>You have received a download containing files from your Surety. To receive this download, click the "Download File" button below.</p>
              </header>
              <div className={styles.statusInfo} style={{ display: 'none' }}>
                <div className={`${styles.statusTag} ${styles.statusTag__viewed}`}>
                  <FontAwesomeIcon icon={faEye} />
                  <div>
                    <h5>Viewed</h5>
                    <p>Viewed <strong>{download?.viewed || 0}</strong> times</p>
                  </div>
                </div>
                {!isMobile && (
                  <div className={styles.statusTag}>
                    <FontAwesomeIcon icon={faDownload} />
                    <div>
                      <h5>Downloaded</h5>
                      <p>Downloaded <strong>{download?.downloaded || 0}</strong> times</p>
                    </div>
                  </div>
                )}
              </div>
              {(download?.additionalFiles?.length > 1) && (
                <div className={styles.summary__container}>
                  <h5 className="rad">Summary</h5>
                  <div className={styles.summary}>
                    <p>Here is a summary of the files that are contained in this download:</p>
                    {_getTable()}
                  </div>
                </div>
              )}
              {(!isMobile && (!download?.additionalFiles?.length || download?.additionalFiles?.length === 1)) && (
                <ButtonPrimary 
                  fullWidth
                  color="navy"
                  icon={faDownload}
                  disabled={isMobile}
                  onClick={() => _doDownloadZip()}
                  tooltip={isMobile ? "Downloading is not supported on mobile devices" : null}
                >
                  Download File
                </ButtonPrimary>
              )}
              {isMobile && (
                <footer>
                  <p>Copies of the notices can be downloaded on a Desktop device.</p>
                </footer>
              )}
            </>
          )}
        </div>
        <div className={styles.poweredBy}>
          <h5 className="rad">Powered by</h5>
          <a href="https://simplybail.com" target="_blank" rel="noreferrer">
            <img src="/images/SimplyBail_logo_long.png" alt="SimplyBail" />
          </a>
        </div>
      </div>
      {isLoading && <Loading />}
      {_getViewPdf()}
    </>
  ) 
}

export default StatusDownload